import { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Film, Palette, Truck, MapPin, Sparkles, Play, ChevronLeft, ChevronRight } from 'lucide-react';
import { SectionReveal } from '@/components/shared/SectionReveal';
import { ProjectCard } from '@/components/shared/ProjectCard';
import { TestimonialsCarousel } from '@/components/TestimonialsCarousel';
import { Separator } from '@/components/ui/separator';
import { getHomepageProjects, getClients, getLatestArticles } from '@/lib/api';
import { getProjectDisplayImage, getProjectAspectRatio } from '@/lib/projectUtils';
import { SEO } from '@/components/SEO';
import { StructuredData } from '@/components/StructuredData';
import { LeadCaptureModal, FollowUpModal } from '@/components/LeadCaptureModal';

// Mexico Advantage Interactive Carousel Component
function MexicoCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedImage, setSelectedImage] = useState(0);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Fetch images from backend
  useEffect(() => {
    const loadGallery = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${backendUrl}/api/mexico-gallery`);
        const data = await response.json();
        // Filter active images and sort by order
        const activeImages = data
          .filter(img => img.active)
          .sort((a, b) => a.order - b.order);
        setImages(activeImages);
      } catch (err) {
        console.error('Failed to load Mexico gallery:', err);
      } finally {
        setLoading(false);
      }
    };
    loadGallery();
  }, []);
  
  // Auto-rotate with crossfade every 5 seconds
  useEffect(() => {
    if (images.length === 0) return;
    const timer = setInterval(() => {
      setSelectedImage((prev) => (prev + 1) % images.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [images]);
  
  if (loading) {
    return (
      <div className="rounded-lg overflow-hidden bg-[hsl(var(--bk-bg))] mb-4">
        <div className="aspect-[4/5] animate-pulse bg-[hsl(var(--bk-bg-elev))]" />
      </div>
    );
  }
  
  if (images.length === 0) return null;
  
  // Visible thumbnails (4 at a time)
  const visibleThumbs = images.slice(currentIndex, currentIndex + 4);
  
  return (
    <div>
      {/* Main Image Window with Ken Burns Effect */}
      <div className="rounded-lg overflow-hidden shadow-[0_24px_80px_rgba(0,0,0,0.6)] mb-4">
        <div className="relative aspect-[4/5] bg-[hsl(var(--bk-bg))]">
          {/* Crossfade between images with Ken Burns pan/zoom */}
          {images.map((img, i) => (
            <img
              key={img.id}
              src={img.image_url}
              alt={img.alt_text || "Film production in Mexico"}
              className={`absolute inset-0 w-full h-full object-cover transition-all duration-[2000ms] ease-out ${
                selectedImage === i 
                  ? 'opacity-100 scale-110 translate-x-[-3%]' 
                  : 'opacity-0 scale-100 translate-x-0'
              }`}
              loading="lazy"
              style={{
                animation: selectedImage === i ? 'kenBurns 5000ms ease-out forwards' : 'none'
              }}
            />
          ))}
        </div>
      </div>
      
      {/* Thumbnail Navigator */}
      <div className="relative flex items-center gap-2">
        {/* Left Arrow */}
        <button
          onClick={() => setCurrentIndex((prev) => Math.max(0, prev - 4))}
          disabled={currentIndex === 0}
          className="w-8 h-8 flex items-center justify-center rounded-full bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-line))] hover:bg-[hsl(var(--bk-fg))] hover:text-[hsl(var(--bk-bg))] transition-all disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronLeft size={16} />
        </button>
        
        {/* Thumbnails */}
        <div className="flex-1 grid grid-cols-4 gap-2">
          {visibleThumbs.map((img, i) => {
            const actualIndex = currentIndex + i;
            return (
              <button
                key={img.id}
                onClick={() => setSelectedImage(actualIndex)}
                className={`aspect-square rounded overflow-hidden border-2 transition-all duration-300 ${
                  selectedImage === actualIndex 
                    ? 'border-[hsl(var(--bk-accent))] scale-95' 
                    : 'border-transparent grayscale hover:grayscale-0 hover:border-[hsl(var(--bk-line))]'
                }`}
              >
                <img src={img.image_url} alt={img.alt_text || ""} className="w-full h-full object-cover" loading="lazy" />
              </button>
            );
          })}
        </div>
        
        {/* Right Arrow */}
        <button
          onClick={() => setCurrentIndex((prev) => Math.min(images.length - 4, prev + 4))}
          disabled={currentIndex >= images.length - 4}
          className="w-8 h-8 flex items-center justify-center rounded-full bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-line))] hover:bg-[hsl(var(--bk-fg))] hover:text-[hsl(var(--bk-bg))] transition-all disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}


export default function HomePage() {
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [articles, setArticles] = useState([]);
  const [showLeadCapture, setShowLeadCapture] = useState(false);
  const [showFollowUp, setShowFollowUp] = useState(false);
  const videoRef = useRef(null);

  // Memoize filtered projects to avoid re-filtering on every render
  const verticalProjects = useMemo(() => 
    projects.filter(p => p.category === 'documentary' || p.category === 'narrative'),
    [projects]
  );

  const commercialProjects = useMemo(() => 
    projects.filter(p => p.category === 'commercial'),
    [projects]
  );

  useEffect(() => {
    Promise.all([getHomepageProjects(), getClients(), getLatestArticles(3)])
      .then(([p, c, a]) => {
        setProjects(p);
        setClients(c);
        setArticles(a);
      });
    
    // Force video play after mount
    if (videoRef.current) {
      videoRef.current.play().catch(() => {
        // Video autoplay prevented by browser - silent fail
      });
    }
  }, []);

  return (
    <div>
      <SEO 
        title="Baklight Films — Film Production Company Guadalajara, Mexico | Production Services & Incentives"
        description="Award-winning film production company in Guadalajara, Mexico. Full-service production for commercials, documentaries, and narrative films. International crews, local expertise, EFICINE-qualified production services."
        path="/"
        keywords="film production mexico, guadalajara production company, mexico production services, film incentives mexico, commercial production mexico, documentary filmmaking mexico, mexico location services, film crew mexico, cinematography mexico, production services guadalajara"
        image="/assets/og-homepage.jpg"
      />
      <StructuredData type="organization" />
      {/* ———— HERO ———— */}
      <section className="relative h-[60vh] min-h-[550px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          {/* 
            VIDEO BACKGROUND - Ready for Sora-generated cinematic B-roll
            To add video: Place MP4 file in /app/frontend/public/assets/hero-video.mp4
            Recommended: Dark, moody, cinematic BTS footage, 1920x1080, looped
          */}
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            className="w-full h-full object-cover"
            poster="https://images.unsplash.com/photo-1727451139462-cd34008cd50b?w=1920&q=85"
          >
            <source src={`${process.env.REACT_APP_BACKEND_URL}/api/video/hero`} type="video/mp4" />
            <source src="/assets/hero-video.mp4" type="video/mp4" />
          </video>
          {/* Fallback image (shown until video loads or if video unavailable) */}
          <img
            src="https://images.unsplash.com/photo-1727451139462-cd34008cd50b?w=1920&q=85"
            alt="Baklight — Cinematic production"
            className="w-full h-full object-cover absolute inset-0 -z-10"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[hsl(222,18%,3%)] via-[hsl(222,18%,4%)]/75 to-[hsl(222,18%,6%)]/30" />
        </div>

        <div className="relative z-10 max-w-[1200px] mx-auto px-5 sm:px-8 w-full pt-32 md:pt-28">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <h1 className="font-display text-5xl sm:text-6xl lg:text-[5.5rem] tracking-[-0.03em] leading-[0.88] text-[hsl(var(--bk-fg))] max-w-[900px]">
              Films. Stories.<br />Images that stay.
            </h1>
            <p className="font-body text-lg md:text-xl text-[hsl(var(--bk-fg-muted))] mt-8 max-w-[560px] leading-[1.6]">
              A director-led production ecosystem in Mexico — from concept to final image.
            </p>
            <p className="font-body text-sm text-[hsl(var(--bk-fg-subtle))] mt-4 max-w-[560px] leading-relaxed">
              We work at the intersection of storytelling, production and finishing — shaping films with precision, intention and cinematic depth.
            </p>
            <div className="flex flex-wrap gap-4 mt-12">
              <Link
                to="/work"
                className="inline-flex items-center gap-2 font-body text-sm px-7 py-3.5 bg-[hsl(var(--bk-fg))] text-[hsl(var(--bk-bg))] rounded-[var(--radius)] hover:opacity-90 transition-opacity active:scale-[0.98]"
              >
                View Work
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 font-body text-sm px-7 py-3.5 border border-[hsl(var(--bk-line-2))] text-[hsl(var(--bk-fg))] rounded-[var(--radius)] hover:bg-[hsl(var(--bk-bg-elev))] transition-colors"
              >
                Start a Project <ArrowRight size={16} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ———— PRODUCTION SERVICES IN MEXICO ———— */}
      <section className="py-16 sm:py-20 bg-[hsl(var(--bk-bg-elev))]">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          <SectionReveal>
            <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
              <div className="flex-1">
                <p className="font-mono-meta text-[hsl(var(--bk-accent-2))] mb-5">Production Services</p>
                <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-[-0.03em] leading-[0.9] text-[hsl(var(--bk-fg))] mb-6">
                  Shoot in Mexico.<br />Keep more of your budget.
                </h2>
                <p className="font-body text-base text-[hsl(var(--bk-fg-muted))] leading-[1.65] mb-6">
                  Up to 30% federal tax credit + 40% state rebate. World-class locations, experienced crews, and streamlined production support — all in one partner.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link
                    to="/mexico"
                    className="inline-flex items-center gap-2 font-body text-sm px-7 py-3 bg-[hsl(var(--bk-accent-2))] text-white rounded-[var(--radius)] hover:opacity-90 transition-opacity"
                    data-testid="prepare-shoot-btn"
                  >
                    Prepare Your Shoot <ArrowRight size={16} />
                  </Link>
                  <Link
                    to="/services"
                    className="inline-flex items-center gap-2 font-body text-sm px-7 py-3 border border-[hsl(var(--bk-line-2))] text-[hsl(var(--bk-fg))] rounded-[var(--radius)] hover:bg-[hsl(var(--bk-bg))] transition-colors"
                  >
                    Explore Production Services <ArrowRight size={16} />
                  </Link>
                  <button
                    onClick={() => setShowLeadCapture(true)}
                    className="inline-flex items-center gap-2 font-body text-sm px-7 py-3 border border-[hsl(var(--bk-line-2))] text-[hsl(var(--bk-fg))] rounded-[var(--radius)] hover:bg-[hsl(var(--bk-bg))] transition-colors"
                    data-testid="download-guide-btn"
                  >
                    Download Mexico Guide
                  </button>
                </div>
              </div>
              <div className="flex-1 w-full">
                <div className="aspect-[4/3] rounded-lg overflow-hidden shadow-[0_24px_80px_rgba(0,0,0,0.6)]">
                  <img
                    src="/assets/mexico/crew-02.jpg"
                    alt="Film production in Mexico"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* ———— WOW MOMENT ———— */}
      <section className="relative h-[50vh] min-h-[350px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/assets/cinema-bw-production.jpg"
            alt="Cinema craft"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40" />
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          className="relative z-10 text-center px-5"
        >
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-[-0.03em] leading-[0.92] text-white">
            Original Films & Stories<br />for Brands
          </h2>
        </motion.div>
      </section>

      {/* ———— ECOSYSTEM INTRO ———— */}
      <section className="py-14 sm:py-18">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          <SectionReveal>
            <p className="font-mono-meta text-[hsl(var(--bk-accent))] mb-5">A Director-Led Production Ecosystem</p>
            <h2 className="font-display text-3xl md:text-5xl lg:text-6xl tracking-[-0.03em] leading-[0.88] text-[hsl(var(--bk-fg))] max-w-[800px]">
              One partner.<br />Fully integrated into your production workflow.
            </h2>
            <p className="font-body text-lg text-[hsl(var(--bk-fg-muted))] mt-8 max-w-[580px] leading-[1.6]">
              Direction, production, post and equipment — integrated to ensure creative consistency and efficient execution at every stage.
            </p>
          </SectionReveal>
        </div>
      </section>

      {/* ———— PACO HERRERA ———— */}
      <section className="py-14 sm:py-18">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          <SectionReveal>
            {/* Responsive floating image with text wrap */}
            <div className="space-y-6">
              {/* Image floats left on larger screens, stacks on mobile */}
              <div className="float-none sm:float-left w-full sm:w-[175px] md:w-[200px] lg:w-[240px] sm:mr-6 mb-6">
                <div className="aspect-[3/4] rounded-lg overflow-hidden shadow-[0_24px_80px_rgba(0,0,0,0.6)]">
                  <img
                    src="https://filmfreeway-production-storage-01-connector.filmfreeway.com/press_kits/headshots/003/639/002/original/2790d4ab42-headshot.jpg?1758508093"
                    alt="Paco Herrera — Director & Cinematographer"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>

              {/* Content wraps around image on larger screens */}
              <div>
                <p className="font-mono-meta text-[hsl(var(--bk-accent))] mb-5">Director & Cinematographer</p>
                <h2 className="font-display text-3xl md:text-4xl tracking-[-0.02em] leading-[0.92] text-[hsl(var(--bk-fg))] mb-6">
                  Paco Herrera
                </h2>
                <p className="font-body text-base text-[hsl(var(--bk-fg-muted))] mb-4 leading-[1.65]">
                  Paco Herrera is a Mexican director and cinematographer whose work explores identity, place and human connection through cinematic language.
                </p>
                <p className="font-body text-base text-[hsl(var(--bk-fg-muted))] mb-4 leading-[1.65]">
                  With over two decades of experience across documentary, narrative and commercial filmmaking, he approaches each project as both a story and a visual construction — shaping tone, rhythm and image from concept through final grade.
                </p>
                <p className="font-body text-sm text-[hsl(var(--bk-fg-subtle))] mb-6 leading-relaxed">
                  His work has been recognized at international film festivals including London Vision Film Festival, Berlin Kiez, Toronto/Los Angeles Documentary Festival and Austin Lift Off.
                </p>
                
                {/* Clear float before link on mobile */}
                <div className="clear-both sm:clear-none">
                  <Link
                    to="/director"
                    className="inline-flex items-center gap-2 font-body text-sm text-[hsl(var(--bk-accent))] hover:opacity-80 transition-opacity"
                  >
                    Full profile <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* ———— SELECTED WORK ———— */}
      <section className="py-14 sm:py-18">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          <SectionReveal>
            <div className="flex items-end justify-between mb-14">
              <div>
                <p className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-3">Selected Work</p>
                <h2 className="font-display text-2xl md:text-3xl tracking-[-0.02em] text-[hsl(var(--bk-fg))]">
                  Not everything. Only what defines our voice.
                </h2>
              </div>
              <Link
                to="/work"
                className="hidden sm:inline-flex items-center gap-2 font-body text-sm text-[hsl(var(--bk-fg-muted))] hover:text-[hsl(var(--bk-fg))] transition-colors"
              >
                View all <ArrowRight size={14} />
              </Link>
            </div>
          </SectionReveal>
          {/* Grid: 6 verticals (top row) + 2 horizontals (bottom row) - 25% larger than previous */}
          <div className="space-y-6">
            {/* All vertical projects - 6 columns */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {verticalProjects.map((project, i) => (
                <SectionReveal key={project.id || project.slug} delay={i * 0.1}>
                  <Link to={`/work/${project.slug}`} className="group block">
                    <div className={`relative ${getProjectAspectRatio(project)} rounded-md overflow-hidden bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-line))] mb-2`}>
                      <img
                        src={getProjectDisplayImage(project)}
                        alt={project.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                    <p className="font-mono-meta text-[10px] text-[hsl(var(--bk-fg-subtle))] uppercase mb-1">{project.category} · {project.year}</p>
                    <h3 className="font-display text-xs text-[hsl(var(--bk-fg))] tracking-tight mb-1 line-clamp-2">{project.title}</h3>
                    <p className="font-body text-[11px] text-[hsl(var(--bk-fg-muted))] line-clamp-2 leading-relaxed">{project.logline}</p>
                  </Link>
                </SectionReveal>
              ))}
            </div>
            
            {/* Commercials - 2 columns */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-6">
              {commercialProjects.map((project, i) => (
                <SectionReveal key={project.id || project.slug} delay={0.4 + i * 0.1}>
                  <Link to={`/work/${project.slug}`} className="group block">
                    <div className={`relative ${getProjectAspectRatio(project)} rounded-lg overflow-hidden bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-line))] mb-3`}>
                      <img
                        src={getProjectDisplayImage(project)}
                        alt={project.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                        <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm border border-white/40 flex items-center justify-center">
                          <Play className="w-5 h-5 text-white fill-white ml-0.5" />
                        </div>
                      </div>
                    </div>
                    <p className="font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))] uppercase mb-1.5">{project.category} · {project.year}</p>
                    <h3 className="font-display text-base text-[hsl(var(--bk-fg))] tracking-tight mb-1.5 line-clamp-2">{project.title}</h3>
                    <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))] line-clamp-1 leading-relaxed">{project.logline}</p>
                  </Link>
                </SectionReveal>
              ))}
            </div>
          </div>
          <div className="sm:hidden mt-8">
            <Link to="/work" className="inline-flex items-center gap-2 font-body text-sm text-[hsl(var(--bk-fg-muted))] hover:text-[hsl(var(--bk-fg))] transition-colors">
              View all work <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ———— RECOGNITION ———— */}
      <section className="py-16 sm:py-20 bg-[hsl(var(--bk-bg-elev))]">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          <SectionReveal>
            <p className="font-mono-meta text-[hsl(var(--bk-accent))] mb-4">Recognition</p>
            <h2 className="font-display text-3xl md:text-5xl tracking-[-0.02em] text-[hsl(var(--bk-fg))] mb-6">
              Recognized internationally.
            </h2>
            <p className="font-body text-base text-[hsl(var(--bk-fg-muted))] mb-16 max-w-[600px]">
              Our work has been recognized across international film festivals and industry awards.
            </p>
          </SectionReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20">
            {/* Film Festivals */}
            <SectionReveal delay={0.1}>
              <h3 className="font-display text-2xl md:text-3xl tracking-[-0.02em] text-[hsl(var(--bk-fg))] mb-8 uppercase">Film Festivals</h3>
              <div className="space-y-5">
                {[
                  { fest: 'Silver Screen Film Festival (Prague)', award: 'Best Short Documentary' },
                  { fest: 'London Vision Film Festival', award: 'Best Short Documentary, Best Cinematography, Best Producer' },
                  { fest: 'Toronto / Los Angeles Documentary Festival', award: 'Best Story' },
                  { fest: 'Austin Lift-Off Film Festival', award: 'Best Short Documentary' },
                  { fest: 'Berlin Kiez Film Festival', award: 'Best Short Documentary' },
                  { fest: 'Berlin Lift-Off Film Festival', award: 'Official Selection' },
                  { fest: 'Hollywood ShortsFest', award: 'Honorable Mention' },
                  { fest: 'Documentaries Without Borders', award: 'Excellence Award' },
                ].map((item, i) => (
                  <div key={i} className="border-b border-[hsl(var(--bk-line))] pb-4 last:border-0">
                    <p className="font-body text-sm text-[hsl(var(--bk-fg))]">{item.fest}</p>
                    <p className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mt-1">{item.award}</p>
                  </div>
                ))}
              </div>
            </SectionReveal>

            {/* Industry Awards */}
            <SectionReveal delay={0.2}>
              <h3 className="font-display text-2xl md:text-3xl tracking-[-0.02em] text-[hsl(var(--bk-fg))] mb-8 uppercase">Industry Awards</h3>
              <div className="space-y-5">
                {[
                  { award: 'Gold Telly Award', category: 'Biography Non-Broadcast (2018)' },
                  { award: 'Silver Telly Award', category: 'Corporate Image Non-Broadcast (2018)' },
                  { award: 'Silver Telly Award', category: 'Corporate Image (2017)' },
                  { award: 'Silver Telly Award', category: 'Art Direction (2017)' },
                  { award: 'Silver Telly Award', category: 'Cinematography (2014)' },
                  { award: 'Bronze Telly Award', category: 'Lifestyle Non-Broadcast (2018)' },
                  { award: 'Bronze Telly Award', category: 'Cinematography (2017, 2014)' },
                  { award: 'Kodak Film Student Scholarship', category: '1999' },
                  { award: 'IMCINE Production Grant', category: 'Mexican Ministry of Culture' },
                ].map((item, i) => (
                  <div key={i} className="border-b border-[hsl(var(--bk-line))] pb-4 last:border-0">
                    <p className="font-body text-sm text-[hsl(var(--bk-fg))]">{item.award}</p>
                    <p className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mt-1">{item.category}</p>
                  </div>
                ))}
              </div>
            </SectionReveal>
          </div>

          {/* Laurels row */}
          <SectionReveal delay={0.3}>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-4 mt-16 pt-12 border-t border-[hsl(var(--bk-line))]">
              {[
                { src: '/laurels/silver-screen.png', alt: 'Silver Screen Film Festival' },
                { src: '/laurels/toronto-la-winner.png', alt: 'Toronto/LA Documentary Festival' },
                { src: '/laurels/berlin-liftoff.png', alt: 'Berlin Lift-Off' },
                { src: '/laurels/hollywood-shorts.png', alt: 'Hollywood ShortsFest' },
                { src: '/laurels/docs-without-borders.png', alt: 'Documentaries Without Borders' },
                { src: '/laurels/bajio-mention.png', alt: 'Bajío Documentary Film Festival' },
              ].map((laurel, i) => (
                <div key={i} className="flex items-center justify-center p-2 opacity-80 hover:opacity-100 transition-opacity">
                  <img
                    src={laurel.src}
                    alt={laurel.alt}
                    className="w-full h-auto max-h-[100px] object-contain invert"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* ———— THREE-COLUMN ECOSYSTEM ———— */}
      <section className="py-14 sm:py-18">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          <SectionReveal>
            <p className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-3">Integrated Services</p>
            <h2 className="font-display text-2xl md:text-3xl tracking-[-0.02em] text-[hsl(var(--bk-fg))] mb-16">
              Production. Post. Rentals — under one continuity.
            </h2>
          </SectionReveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Film, title: 'Production', desc: 'International shoots, crews, locations and full production support across Mexico.', link: '/services' },
              { icon: Palette, title: 'Post & Finishing', desc: 'Color grading and finishing workflows that define the final cinematic language.', link: '/post' },
              { icon: Truck, title: 'Equipment & Rentals', desc: 'Camera systems, grip trucks and infrastructure ready for productions of any scale.', link: '/rentals' },
            ].map((item, i) => (
              <SectionReveal key={item.title} delay={i * 0.1}>
                <Link to={item.link} className="group block p-8 rounded-lg border border-[hsl(var(--bk-line))] bg-[hsl(var(--bk-bg-elev))] hover:border-[hsl(var(--bk-line-2))] transition-colors h-full">
                  <item.icon size={28} className="text-[hsl(var(--bk-accent))] mb-6" strokeWidth={1.5} />
                  <h3 className="font-display text-xl text-[hsl(var(--bk-fg))] mb-3">{item.title}</h3>
                  <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))] leading-relaxed">{item.desc}</p>
                  <span className="inline-flex items-center gap-1 font-mono-meta text-[hsl(var(--bk-accent))] mt-6 group-hover:gap-2 transition-all">
                    Explore <ArrowRight size={12} />
                  </span>
                </Link>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ———— TRUST / CLIENTS ———— */}
      <section className="py-12 sm:py-16 bg-[hsl(var(--bk-bg-elev))]">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          <SectionReveal>
            <p className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-3">Trusted By</p>
            <h2 className="font-display text-2xl md:text-3xl tracking-[-0.02em] text-[hsl(var(--bk-fg))] mb-14">
              Trusted by international brands, agencies and filmmakers.
            </h2>
          </SectionReveal>
          <SectionReveal delay={0.1}>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-8 items-center">
              {[
                { name: 'HBO', logo: '/assets/clients-final/hbo-wiki.svg' },
                { name: 'Discovery Channel', logo: '/assets/clients-final/discovery.png' },
                { name: 'National Geographic', logo: '/assets/clients-final/natgeo.png' },
                { name: 'Nestlé', logo: '/assets/clients-final/nestle-wiki.svg' },
                { name: 'Gatorade', logo: '/assets/clients-final/gatorade.png' },
                { name: 'Bacardi', logo: '/assets/clients-final/bacardi.png' },
                { name: 'Sony', logo: '/assets/clients-final/sony.png' },
                { name: 'Ogilvy', logo: '/assets/clients-final/ogilvy.png' },
                { name: 'Anima Studios', logo: '/assets/clients-final/anima.png' },
                { name: 'Publicis Sapient', logo: '/assets/clients-final/publicis.png' },
                { name: 'Mullen Lowe', logo: '/assets/clients-final/mullenlowe.png' },
                { name: 'The Patrón Spirits Company', logo: '/assets/clients-final/patron.png' },
              ].map((client, i) => (
                <div
                  key={i}
                  className="flex items-center justify-center p-4 opacity-60 hover:opacity-100 transition-opacity duration-500"
                  data-testid={`client-logo-${i}`}
                >
                  <img
                    src={client.logo}
                    alt={client.name}
                    className="w-full h-auto max-h-[56px] object-contain brightness-0 invert"
                    loading="lazy"
                    onError={(e) => { 
                      // Hide broken logo images
                      e.target.style.opacity = '0.2'; 
                    }}
                  />
                </div>
              ))}
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* Testimonials Carousel */}
      <TestimonialsCarousel />

      {/* ———— COLLABORATORS ———— */}
      <section className="py-10 sm:py-14">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          <SectionReveal>
            <p className="font-mono-meta text-[hsl(var(--bk-accent))] mb-4">Collaborators</p>
            <h2 className="font-display text-3xl md:text-4xl tracking-[-0.02em] text-[hsl(var(--bk-fg))] mb-6">
              A trusted production network built over decades.
            </h2>
          </SectionReveal>

          {/* Roles Grid */}
          <SectionReveal delay={0.1}>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-8">
              {[
                'Producers',
                'ADs',
                'Camera operators',
                'Gaffers',
                'Grips',
                'Production Designers',
                'Sound guys',
                'Hair & Makeup',
                'PAs',
                'Fixers',
                'Drone Pilots',
                'Stylists',
                'Scripts',
                'DITs',
                'ACs',
              ].map((role, i) => (
                <div
                  key={i}
                  className="px-4 py-3 rounded-lg bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-line))] hover:border-[hsl(var(--bk-accent))]/30 transition-colors text-center"
                >
                  <p className="font-body text-sm text-[hsl(var(--bk-fg))]">{role}</p>
                </div>
              ))}
            </div>
            <div className="text-center space-y-1">
              <p className="font-body text-base text-[hsl(var(--bk-fg-muted))]">
                Bilingual international-experienced crews.
              </p>
              <p className="font-mono-meta text-sm text-[hsl(var(--bk-fg-subtle))]">
                Throughout Mexico
              </p>
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* ———— MEXICO ADVANTAGE ———— */}
      <section className="py-16 sm:py-20 bg-[hsl(var(--bk-bg-elev))]">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-center">
            <SectionReveal>
              <p className="font-mono-meta text-[hsl(var(--bk-accent-2))] mb-5">The Mexico Advantage</p>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-[-0.03em] leading-[0.88] text-[hsl(var(--bk-fg))]">
                World-class locations.<br />Local expertise.<br />International standards.
              </h2>
              <p className="font-body text-base text-[hsl(var(--bk-fg-muted))] mt-8 leading-[1.65]">
                Mexico offers an extraordinary range of locations, skilled crews and production flexibility.
              </p>
              <p className="font-body text-base text-[hsl(var(--bk-fg-muted))] mt-4 leading-[1.65]">
                Baklight connects international productions with local expertise and global standards — ensuring creative ambition is matched with reliable execution.
              </p>
              <Link
                to="/mexico"
                className="inline-flex items-center gap-2 font-body text-sm text-[hsl(var(--bk-accent-2))] mt-10 hover:opacity-80 transition-opacity"
              >
                Explore Production in Mexico <ArrowRight size={14} />
              </Link>
            </SectionReveal>
            <SectionReveal delay={0.15}>
              {/* Interactive Image Carousel - 25 curated Mexico images */}
              <MexicoCarousel />
            </SectionReveal>
          </div>
        </div>
      </section>

      {/* ———— INNOVATION / AI ———— */}
      <section className="py-14 sm:py-18">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          <SectionReveal>
            <div className="max-w-[680px]">
              <Sparkles size={28} className="text-[hsl(var(--bk-accent))] mb-6" strokeWidth={1.5} />
              <p className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-4">Innovation</p>
              <h2 className="font-display text-3xl md:text-4xl tracking-[-0.02em] leading-[0.92] text-[hsl(var(--bk-fg))]">
                Shaping the future of filmmaking.
              </h2>
              <p className="font-body text-base text-[hsl(var(--bk-fg-muted))] mt-6 leading-[1.65]">
                We integrate AI-driven tools and hybrid workflows to expand creative possibilities — always in service of story, never as a substitute for it.
              </p>
              <Link
                to="/innovation"
                className="inline-flex items-center gap-2 font-body text-sm text-[hsl(var(--bk-accent))] mt-8 hover:opacity-80 transition-opacity"
              >
                Explore our approach <ArrowRight size={14} />
              </Link>
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* ———— LATEST ARTICLES ———— */}
      {articles.length > 0 && (
        <section className="py-20 sm:py-28 bg-[hsl(var(--bk-bg))]">
          <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
            <SectionReveal>
              <div className="flex items-end justify-between mb-12">
                <div>
                  <p className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-3">Journal</p>
                  <h2 className="font-display text-3xl md:text-4xl tracking-[-0.02em] text-[hsl(var(--bk-fg))]">
                    Latest Articles
                  </h2>
                </div>
                <Link
                  to="/journal"
                  className="hidden sm:inline-flex items-center gap-2 font-body text-sm text-[hsl(var(--bk-fg-muted))] hover:text-[hsl(var(--bk-fg))] transition-colors"
                >
                  View all <ArrowRight size={14} />
                </Link>
              </div>
            </SectionReveal>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
              {articles.map((article, i) => (
                <SectionReveal key={article.id} delay={i * 0.1}>
                  <Link to={`/journal/${article.slug}`} className="group block">
                    {article.hero_image && (
                      <div className="relative aspect-[4/3] rounded-lg overflow-hidden bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-line))] mb-4">
                        <img
                          src={article.hero_image}
                          alt={article.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                      </div>
                    )}
                    <p className="font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))] uppercase mb-2">
                      {new Date(article.published_at).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </p>
                    <h3 className="font-display text-xl text-[hsl(var(--bk-fg))] tracking-tight mb-2 group-hover:text-[hsl(var(--bk-accent))] transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                    {article.excerpt && (
                      <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))] leading-relaxed line-clamp-3">
                        {article.excerpt}
                      </p>
                    )}
                  </Link>
                </SectionReveal>
              ))}
            </div>

            <div className="sm:hidden mt-8 text-center">
              <Link to="/journal" className="inline-flex items-center gap-2 font-body text-sm text-[hsl(var(--bk-fg-muted))] hover:text-[hsl(var(--bk-fg))] transition-colors">
                View all articles <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ———— FINAL CTA ———— */}
      <section className="py-28 sm:py-40 bg-[hsl(var(--bk-bg-elev))]">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 text-center">
          <SectionReveal>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-[-0.03em] leading-[0.88] text-[hsl(var(--bk-fg))]">
              Let's build something cinematic.
            </h2>
            <p className="font-body text-base md:text-lg text-[hsl(var(--bk-fg-muted))] mt-6 max-w-[520px] mx-auto leading-[1.6]">
              From concept to final image — we partner with filmmakers, brands and agencies to create films that resonate.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mt-12">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 font-body text-sm px-8 py-3.5 bg-[hsl(var(--bk-fg))] text-[hsl(var(--bk-bg))] rounded-[var(--radius)] hover:opacity-90 transition-opacity active:scale-[0.98]"
              >
                Start a Project <ArrowRight size={16} />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 font-body text-sm px-8 py-3.5 border border-[hsl(var(--bk-line-2))] text-[hsl(var(--bk-fg))] rounded-[var(--radius)] hover:bg-[hsl(var(--bk-bg))] transition-colors"
              >
                Contact
              </Link>
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* Lead Capture & Follow-up Modals */}
      <LeadCaptureModal 
        isOpen={showLeadCapture} 
        onClose={() => setShowLeadCapture(false)}
        onSuccess={() => setShowFollowUp(true)}
      />
      <FollowUpModal 
        isOpen={showFollowUp} 
        onClose={() => setShowFollowUp(false)}
      />
    </div>
  );
}
